import React, { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { getMapProvider } from "../../config/mapProviders";
import { ExtentPickerProps } from "./types";
import RasterMapsExtentPicker from "./RasterMapsExtentPicker";

export type { ExtentPickerProps } from "./types";
export { RADIUS_MIN_M, RADIUS_MAX_M, RADIUS_DEFAULT_M, extentBounds } from "./types";

// mapbox-gl is ~800 KB gzipped. Only pay for it when Mapbox is the active
// provider AND a map is actually on screen.
const MapboxMapExtentPicker = lazy(() => import("./MapboxMapExtentPicker"));
const GoogleMapsExtentPicker = lazy(() => import("./GoogleMapsExtentPicker"));

const Skeleton: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <div
    className={`w-full animate-pulse rounded-lg border border-crane-500/25 bg-crane-500/[0.06] ${
      compact ? "h-44" : "h-60"
    }`}
  />
);

/**
 * Picks the renderer for the configured provider. Call sites stay renderer-agnostic:
 * they pass an extent and get a picker back, whichever engine draws it.
 */
const AddressExtentPicker: React.FC<ExtentPickerProps> = (props) => {
  const provider = getMapProvider();
  const [expanded, setExpanded] = useState(false);

  /*
   * Expanding must not remount the renderer. Google Maps bills per *map load*,
   * so a remount is a second charge and a replayed intro flight — which is what
   * a portal costs, because moving an element to a different tree position
   * unmounts it. So the renderer keeps one fixed position in the tree and only
   * the wrapper's classes change.
   *
   * That works because `position: fixed` ignores an ancestor's max-height and
   * overflow. The one thing it cannot ignore is a stacking context, and the
   * composer that hosts this control is `sticky`, which always opens one — see
   * the z-index note on the composer in ReconstructionAgent.
   *
   * The floating widget is excluded: its root carries a backdrop-filter, which
   * *does* become a containing block for fixed descendants, so an overlay there
   * would be trapped inside a 480px panel. `compact` is true only in that
   * variant, so expansion is simply not offered.
   */
  const canExpand = !props.readOnly && !props.compact;

  /*
   * Going fixed takes the control out of flow, so the page it was sitting in
   * gets shorter by exactly its height. The browser then clamps scrollTop to
   * the new maximum, and collapsing restores the height but not the scroll —
   * which is why the conversation came back scrolled down, with its last
   * messages hidden behind the composer. Reserving the vacated height keeps the
   * document the same length throughout, and the saved offset covers any
   * clamping that still happens.
   */
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [reservedH, setReservedH] = useState(0);
  const scrollYRef = useRef(0);

  const toggleExpand = useCallback(() => {
    if (!expanded) {
      scrollYRef.current = window.scrollY;
      setReservedH(rootRef.current?.offsetHeight ?? 0);
    }
    setExpanded((v) => !v);
  }, [expanded]);

  useLayoutEffect(() => {
    if (expanded || reservedH === 0) return;
    window.scrollTo({ top: scrollYRef.current });
    setReservedH(0);
  }, [expanded, reservedH]);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setExpanded(false); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [expanded]);

  // The renderer owns the toggle in both states, so the caption and the button
  // stay on one row whether the map is inline or fullscreen. The label and the
  // button's weight flip on `expanded`; the overlay adds no control of its own.
  const renderer = (
    <>
      {provider.renderer === "mapbox" && (
        <Suspense fallback={<Skeleton compact={props.compact} />}>
          <MapboxMapExtentPicker
            {...props}
            expanded={expanded}
            onToggleExpand={canExpand ? toggleExpand : undefined}
          />
        </Suspense>
      )}
      {provider.renderer === "google" && (
        <Suspense fallback={<Skeleton compact={props.compact} />}>
          <GoogleMapsExtentPicker
            {...props}
            expanded={expanded}
            onToggleExpand={canExpand ? toggleExpand : undefined}
          />
        </Suspense>
      )}
      {provider.renderer === "raster" && (
        <RasterMapsExtentPicker
          {...props}
          expanded={expanded}
          onToggleExpand={canExpand ? toggleExpand : undefined}
        />
      )}
    </>
  );

  return (
    <div ref={rootRef}>
      {/* Holds the space the fixed overlay vacates. Always rendered so the
          renderer's position among its siblings never shifts. */}
      <div aria-hidden style={{ height: expanded ? reservedH : 0 }} />
      <div
      role={expanded ? "dialog" : undefined}
      aria-modal={expanded || undefined}
      aria-label={expanded ? "Reconstruction area" : undefined}
      className={expanded
        ? "fixed inset-0 z-[100] flex flex-col gap-3 bg-ink-950 p-3 pb-6 sm:p-5 sm:pb-8"
        : undefined}
    >
      <div className={expanded ? "flex min-h-0 flex-1 flex-col" : undefined}>{renderer}</div>
      </div>
    </div>
  );
};

export default AddressExtentPicker;
